'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  teamMember,
  YEAR_ACTIVITIES,
  YEAR_ACTIVITY_PCT,
  YEAR_PLANNING,
  YEAR_WALL_FILTERS,
  type PlanningCell,
  type PlanningState,
} from '../demo-data'
import { monthLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader, BoardName, HeaderStat, PlanningPill } from './app-screen'
import styles from './planning-wall-visual.module.css'
import { HERO_ENTER_MS, useScenario } from './use-scenario'

const OWNER_INITIALS = YEAR_WALL_FILTERS.owner.initials
const OWNER = teamMember(OWNER_INITIALS)
const MONTH = YEAR_WALL_FILTERS.month.label

const OPTIONS = YEAR_WALL_FILTERS.options

/* Each column carries a short form for the phone-width layout, where `Jaarrekening` does not fit. */
const columns = (t: VisualCopy) =>
  YEAR_ACTIVITIES.map((activity) => ({
    label: (
      <>
        <span className={styles.wideOnly}>{t.yearActivity[activity.key].label}</span>
        <span className={styles.narrowOnly}>{t.yearActivity[activity.key].short}</span>
      </>
    ),
    pct: YEAR_ACTIVITY_PCT[activity.key],
  }))

/*
 * Card-sized throughout, like every other visual on the page: nine rows leave about 24px each, and
 * hero-scale cells are twice that — they overlapped their own rows.
 */
/** Resolved in CSS, so a container query can restate it for a phone-width card (see the module). */
const BOARD_GRID = 'var(--wall-cols)'

/**
 * How long each beat stays on screen.
 *
 * This story used to run fourteen beats at a flat 900ms, and six of them — 4, 7, 9, 10, 11 and 13 —
 * rendered exactly what the beat before them rendered: 5.4 seconds of a 12.6-second loop in which
 * the picture did not change, most of it in one stretch of a filtered board nobody was touching.
 * What is left is seven beats that each move something, and the pace now says what kind of move it
 * is. Ticking a box is a click and passes quickly; the board answering it is the point and is held;
 * the filtered board with the panel lifted off it is what the story was built to arrive at, so it is
 * held longest of the seven.
 *
 * Beat 0 is held nearly as long, and deliberately. Two filters leave five rows in a card that fits
 * nine, so the end of the story is also the emptiest thing on screen — it should not be the picture
 * the hero spends most of its time showing. The whole wall is.
 */
const BEAT_HOLDS = [1500, 800, 560, 1200, 560, 1100, 1600] as const

const REST_BEAT = 0 // SSR / reduced-motion snapshot — the whole wall, which is the hero's image

const PANEL_FROM = 1 // the filter panel opens
const OWNER_CHECKED_AT = 2 // the colleague is ticked
const OWNER_APPLIED_AT = 3 // rows outside their client cluster collapse away
const MONTH_CHECKED_AT = 4
const MONTH_APPLIED_AT = 5
const PANEL_TO = 6 // the panel closes; the filters stay on as chips

/*
 * Clearing the filters is the loop's wrap, not a beat of its own.
 *
 * Beat 0 is the unfiltered wall and the loop returns there, so the chips drop and the whole board
 * comes back on the same stroke that starts the story again — one transition doing both jobs. The
 * old table spent a beat clearing, a beat reading as the start, and three more before that holding
 * a board no one was doing anything to.
 */

/** A cell's state at rest — the board is already full here, so animated cells show their `from`. */
function toneOf(cell: PlanningCell): PlanningState {
  return typeof cell === 'string' ? cell : cell.from
}

/** The board's filter control — a funnel, as in the app; a magnifier would mean search. */
function FunnelIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M2 3h12l-4.6 5.3v4.2L6.6 14V8.3L2 3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden>
      <path
        d="M1.5 5.2 3.9 7.5 8.5 2.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** A checkbox in the filter panel, ticked once the scenario reaches it. */
function FilterOption({ label, checked, dimmed }: { label: string; checked: boolean; dimmed?: boolean }) {
  return (
    <div className={clsx(styles.option, dimmed && styles.optionDimmed)}>
      <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)}>{checked && <CheckIcon />}</span>
      <span className="truncate">{label}</span>
    </div>
  )
}

/**
 * Planning-page hero — the wall being *used* rather than filled. The board starts complete; the
 * filter panel opens, a colleague is ticked so the board narrows to their client cluster, then a
 * deadline month narrows it again — with the client count and completion recomputing on each step,
 * as the board's own header does. Then the filters drop and the whole wall returns.
 *
 * Deliberately a different story from the homepage hero, which fills the same wall: this one is
 * about finding your own work in it. `beat === null` (SSR, no-JS, reduced motion) rests on the
 * whole wall rather than on a filtered slice: without motion, the hero's image is the wall.
 */
export function PlanningWallVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT, enterHoldMs: HERO_ENTER_MS })
  const effBeat = beat ?? REST_BEAT

  const panelOpen = effBeat >= PANEL_FROM && effBeat < PANEL_TO
  const ownerChecked = effBeat >= OWNER_CHECKED_AT
  const ownerApplied = effBeat >= OWNER_APPLIED_AT
  const monthChecked = effBeat >= MONTH_CHECKED_AT
  const monthApplied = effBeat >= MONTH_APPLIED_AT
  // The beat a filter lands on is the beat the two figures on the bar recompute. Both are the
  // answer to the tick before them, and at 10px a number swapped in place says nothing happened.
  const recount = effBeat === OWNER_APPLIED_AT || effBeat === MONTH_APPLIED_AT

  const stat = monthApplied ? YEAR_WALL_FILTERS.month : ownerApplied ? YEAR_WALL_FILTERS.owner : YEAR_WALL_FILTERS.none

  return (
    <AppUiVisual label={t.screen.planningWall.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="planning-wall" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.jaarplanning.title}
            detail={t.period.closingBookYear}
            right={
              <span className={clsx(styles.stat, recount && styles.recount)}>
                <HeaderStat value={`${stat.donePct}%`} label={t.word.complete} />
              </span>
            }
          />

          {/* The board's own filter bar: what is currently narrowing the wall, and how much of the
              client base is left — the line the real board keeps at the top right. */}
          <div className={styles.filterBar}>
            <span className={clsx(styles.filterIcon, panelOpen && styles.filterIconActive)} aria-hidden="true">
              <FunnelIcon />
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className={clsx(styles.chip, ownerApplied && styles.chipVisible)}>
                <span className={styles.chipAvatar} style={{ background: OWNER.color }}>
                  {OWNER_INITIALS}
                </span>
                {OWNER.name}
              </span>
              <span className={clsx(styles.chip, monthApplied && styles.chipVisible)}>{monthLabel(t, MONTH)}</span>
            </div>
            {/* Only a narrowed board counts itself against the whole base. Before a filter lands
                the two numbers are the same one, and "812 of 812" reads as a counter stuck at
                100% rather than as the client base it is. */}
            <span className={clsx(styles.clientCount, recount && styles.recount)}>
              {ownerApplied || monthApplied ? (
                <>
                  <b>{stat.clients}</b> {t.word.of} {YEAR_WALL_FILTERS.none.clients} {t.word.clientsLower}
                </>
              ) : (
                <>
                  <b>{YEAR_WALL_FILTERS.none.clients}</b> {t.word.clientsLower}
                </>
              )}
            </span>
          </div>

          <div className={clsx(styles.board, 'flex min-h-0 flex-1 flex-col')}>
            <AppSubHeader
              rowLabel={t.word.client.toUpperCase()}
              columns={[
                { label: 'RE.', className: clsx(styles.tightHead, styles.hideNarrow) },
                {
                  label: t.screen.planningWall.month,
                  align: 'left' as const,
                  className: clsx(styles.tightHead, styles.hideNarrow),
                },
                ...columns(t),
              ]}
              style={{ gridTemplateColumns: BOARD_GRID }}
            />

            <div className={clsx(styles.body, 'min-h-0 flex-1')}>
              {YEAR_PLANNING.map((row) => {
                const owned = row.owner === OWNER_INITIALS
                const inMonth = row.deadline === MONTH
                const hidden = (ownerApplied && !owned) || (monthApplied && !inMonth)
                return (
                  <div
                    key={row.nr}
                    className={clsx(styles.row, hidden && styles.rowHidden)}
                    style={{ gridTemplateColumns: BOARD_GRID }}
                  >
                    <div className="flex min-w-0 items-center">
                      <BoardName nr={row.nr} indent={Boolean(client(row.nr).parent)} showTag={false} />
                    </div>
                    <span
                      className={clsx(styles.owner, styles.hideNarrow)}
                      style={{ background: teamMember(row.owner).color }}
                    >
                      {row.owner}
                    </span>
                    <span className={clsx(styles.deadline, styles.hideNarrow)}>{monthLabel(t, row.deadline)}</span>
                    {row.cells.map((cell, index) => (
                      <PlanningPill key={YEAR_ACTIVITIES[index].key} tone={toneOf(cell)} className={styles.cell} />
                    ))}
                  </div>
                )
              })}
            </div>

            {/* The filter popover the board opens: colleague, month, tag — hanging straight off the
                bottom of the bar it was opened from, at the left edge of the funnel that opens it.
                It is a child of the board, which begins at that bar, so the offset is four pixels
                rather than a hand-written sum of the header and filter-bar heights — which is how
                it ended up 37px too high, over the four activity headings.

                It covers the head of the client column and the rows under it while it is open, and
                that is the trade a popover makes to belong to its control: pinned to the right edge
                instead, it was as far from the funnel as this card allows and read as a separate
                screen. */}
            <div className={clsx(styles.panel, panelOpen && styles.panelOpen)} aria-hidden="true">
              <div className={clsx(styles.panelCol, styles.panelPeople)}>
                <span className={styles.panelHead}>{t.screen.planningWall.searchUser}</span>
                {OPTIONS.people.map((initials) => (
                  <FilterOption
                    key={initials}
                    label={teamMember(initials).name}
                    checked={initials === OWNER_INITIALS && ownerChecked}
                    dimmed={initials !== OWNER_INITIALS}
                  />
                ))}
              </div>
              <div className={clsx(styles.panelCol, styles.panelMonths)}>
                <span className={styles.panelHead}>{t.screen.planningWall.searchMonth}</span>
                {OPTIONS.months.map((month) => (
                  <FilterOption
                    key={month}
                    label={monthLabel(t, month)}
                    checked={month === MONTH && monthChecked}
                    dimmed={month !== MONTH}
                  />
                ))}
              </div>
              {/* Dropped at phone width, where two columns fit and the story only ever uses two.
                  The page's own bullet promises filtering on colleague, month or tag, so it stays
                  wherever there is room for it. */}
              <div className={clsx(styles.panelCol, styles.panelTags)}>
                <span className={styles.panelHead}>{t.screen.planningWall.searchTags}</span>
                {OPTIONS.tags.map((tag) => (
                  <FilterOption key={tag} label={tag} checked={false} dimmed />
                ))}
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
